import { useEffect, useState } from 'react';
import { Modal, ButtonV3, CheckboxV5 } from 'react-ui-lib';
import './_publishFilter.scss'

/**
 * PublishFilter Component
 * A modal component that allows users to select and publish builds for different sources
 * 
 * @param {Object} props
 * @param {Array} props.sourceList - List of available sources to publish
 * @param {boolean} props.openPublishModal - Controls modal visibility
 * @param {Function} props.setOpenPublishModal - Function to control modal state
 * @param {Function} props.onPublishSave - Callback function when sources are published
 * @param {Array} props.defaultSelectedSource - Pre-selected sources
 */
const PublishFilter = (props) => {
    const {
        type = 'publish',
        sourceList = [],
        openPublishModal = true,
        setOpenPublishModal = () => { },
        onPublishSave = () => { },
        defaultSelectedSource = [],
    } = props;

    // State management
    const [selectedSource, setSelectedSource] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);

    // Initialize selected sources when default sources change
    useEffect(() => {
        // selectAllSources();
        const allSourceNames = sourceList.map(source => source.sourceName);
        setSelectedSource(allSourceNames);
        setIsAllSelected(sourceList.length === allSourceNames.length);
    }, [sourceList.length]);

    /**
     * Handles modal close action
     */
    const onClose = () => {
        setOpenPublishModal(false);
    };

    /**
     * Toggles selection of all sources
     * If all sources are currently selected, deselects all
     * If some or none are selected, selects all
     */
    const selectAllSources = () => {
        if (isAllSelected) {
            setSelectedSource([]);
            setIsAllSelected(false);
        } else {
            const allSourceNames = sourceList.map(source => source.sourceName);
            setSelectedSource(allSourceNames);
            setIsAllSelected(true);
        }
    };

    /**
     * Toggles selection of a single source
     * @param {string} sourceName - Name of the source to toggle
     */
    const selectTheSource = (sourceName) => {
        const updatedSource = [...selectedSource];
        const index = selectedSource.findIndex((item) => item === sourceName);
        if (index > -1) {
            updatedSource.splice(index, 1);
        } else {
            updatedSource.push(sourceName);
        }

        setSelectedSource(updatedSource);
        setIsAllSelected(sourceList.length === updatedSource.length);
    };

    return (
        <>
            <Modal
                isOpen={openPublishModal}
                onClose={onClose}
                header={{
                    title: type === "publish" ? "Publish Builds" : "Local Builds",
                    closeOnOutsideClick: true,
                    style: {
                        backgroundColor: "white",
                        color: "#333333",
                        padding: "0.685rem 1rem",
                        fontSize: "1.5rem",
                        fontWeight: "700",
                        borderBottom: "1px solid rgba(213, 220, 229, 1)",
                    },
                }}
                customStyle={{
                    width: "95%",
                    maxWidth: "25rem",
                    maxHeight: "calc(100% - 8rem)",
                }}
                modalBodyStyle={{
                    padding: "1rem",
                    maxHeight: "30rem",
                }}
                hideFooter={false}
                footer={{
                    customStyle: {
                        justifyContent: "space-between",
                        padding: "0.6875rem 1.25rem",
                        color: "#333333",
                    },
                    newBtn: {
                        props: {
                            children: (
                                <>
                                    <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
                                        {selectedSource.length} sources selected
                                    </div>
                                    <div style={{ display: "flex", gap: "1rem" }}>
                                        <ButtonV3
                                            name={"Cancel"}
                                            onClick={onClose}
                                            btnStyleType={"secondary"}
                                        />
                                        <ButtonV3
                                            name={"Publish"}
                                            onClick={() => {
                                                onPublishSave(selectedSource, () => onClose());
                                            }}
                                            disabled={selectedSource.length <= 0}
                                        />
                                    </div>
                                </>
                            ),
                        },
                    },
                }}
            >
                <div className="publish_source_modal">
                    <p className="publish_source_modal_label">
                        Choose the sources you want to build and publish
                    </p>
                    <ul className="publish_source_modal__selected_list">
                        {/* Select All Checkbox */}
                        { sourceList.length > 1 ?
                        <li
                            key={`selected_source_all`}
                            className="publish_source_modal__selected_list_item selected_list_item_all"
                        >
                            <CheckboxV5
                                key={`source_all_cb`}
                                labelName={'Select All'}
                                title={'Select All'}
                                checked={isAllSelected}
                                dataAttr={isAllSelected ? "true" : "false"}
                                onClickHandling={selectAllSources}
                                containerStyle={{}}
                                labelStyle={{
                                    maxWidth: "8rem",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                }}
                            />
                            <span>{selectedSource.length}/{sourceList.length}</span>
                        </li>
                        :
                        <></>
                        } 

                        {/* Individual Source Checkboxes */}
                        {sourceList.map((source, index) => {
                            const isSelected = selectedSource.includes(source.sourceName);
                            return (
                                <li
                                    key={`selected_source_${index}`}
                                    className="publish_source_modal__selected_list_item"
                                >
                                    <CheckboxV5
                                        key={`source_${source.sourceName}_cb`}
                                        labelName={
                                            <>{source.sourceName} ({(source.sourceLangISO || 'en').toUpperCase()})</>
                                        }
                                        title={source.sourceName}
                                        checked={isSelected}
                                        dataAttr={isSelected ? "true" : "false"}
                                        onClickHandling={() => selectTheSource(source.sourceName)}
                                        containerStyle={{}}
                                        labelStyle={{
                                            maxWidth: "8rem",
                                            overflow: "hidden",
                                            textOverflow: "ellipsis",
                                        }}
                                        disabled={sourceList.length === 1}
                                    />
                                </li>
                            );
                        })}
                    </ul>
                </div>
            </Modal>
        </>
    )
}

export default PublishFilter